import { getContactsData } from "selectors/contacts";
import {
  getCurrentXeroOrganizationId,
  getSelectedBusinessBookCloseDate,
  getSelectedBusinessData,
  getSelectedBusinessIntegrationServiceData
} from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import Arrays from "utils/Arrays";
import AuditActions from "actions/AuditActions";
import DateTimeFormats from "const/DateTimeFormats";
import Objects from "utils/Objects";
import Texts from "utils/Texts";
import TransactionFieldsToEdit from "const/TransactionFieldsToEdit";
import Transactions from "utils/Transactions";
import TransactionsActions from "actions/TransactionsActions";
import moment from "moment";
import useShowCommonModal from "./useShowCommonModal";

const updateTransactionsState = ({
  data,
  prevState,
  rpaExcludedWords,
  lastGptAiFineTuneStatus
}) => {
  if (!data) return prevState;

  const stateIds = prevState.map(({ id }) => id);

  return [
    ...prevState,
    ...data.filter(({ id }) => !stateIds.includes(id)).map((transactionData) => {
      const foundState = Arrays.findById(prevState, transactionData.id);

      return foundState || Transactions.getTransactionState(transactionData, { lastGptAiFineTuneStatus, rpaExcludedWords });
    })
  ];
};

const useAuditUtils = ({ transactionsData, currentRoute, refetchData }) => {
  const dispatch = useDispatch();

  const showCommonModal = useShowCommonModal();

  const { uiTexts, messages } = useSelector(getTextsData);

  const selectedBusinessIntegrationServiceData = useSelector(getSelectedBusinessIntegrationServiceData);

  const [transactionsState, setTransactionsState] = useState([]);

  const [selectedIds, setSelectedIds] = useState([]);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const {
    extraData: { integrationService, lastGptAiFineTuneStatus } = {},
    settings: { rpaExcludedWords }
  } = useSelector(getSelectedBusinessData);

  const selectedBusinessBookCloseDate = useSelector(getSelectedBusinessBookCloseDate);

  const contactsData = useSelector(getContactsData);

  const closedBookDateTransactionIds = useMemo(() => {
    if (!selectedBusinessBookCloseDate || !transactionsData) return [];

    return transactionsData.filter(({ timestamp }) => {
      return moment(timestamp).isSameOrBefore(moment(selectedBusinessBookCloseDate));
    }).map(({ id }) => id);
  }, [selectedBusinessBookCloseDate, transactionsData]);

  const transactionIds = useMemo(() => {
    return transactionsData ? transactionsData.map(({ id }) => id) : [];
  }, [transactionsData]);

  const allItemsSelected = !!transactionIds?.length && transactionIds.every((id) => selectedIds.includes(id));

  const bulkMarkAsRevised = useCallback(async(transactionsIds, newTransactionsState) => {
    const invalidContactChange = !xeroBusiness && transactionsIds.some((id) => {
      const transactionState = Arrays.findById(newTransactionsState, id);

      const transactionData = Arrays.findById(transactionsData, id);

      return transactionState && !Transactions.validateContactChange(transactionState, transactionData);
    });

    if (invalidContactChange) {
      showCommonModal({ text: uiTexts.selectContact, headerText: uiTexts.warning });

      return false;
    }

    const payload = xeroBusiness ? [] : transactionsIds.map((id) => {
      const transactionState = Arrays.findById(newTransactionsState, id);

      const transactionData = Arrays.findById(transactionsData, id);

      if (!transactionState) return null;

      const changedFields = Transactions.getChangedFields(transactionState, transactionData);

      return changedFields && { id, ...changedFields };
    }).filter(Boolean);

    if (payload.length) {
      if (payload.some(({ id }) => closedBookDateTransactionIds.includes(id))) {
        const closedBookTimeText = moment.utc(selectedBusinessBookCloseDate).format(DateTimeFormats.DATE_TEXT_EXT);

        showCommonModal({
          text: Texts.replaceVars(messages.transactionsClosedBookWarning, {
            date: closedBookTimeText,
            service: selectedBusinessIntegrationServiceData.label
          }),
          headerText: uiTexts.warning
        });

        return false;
      }
      await dispatch(TransactionsActions.bulkTransactionsUpdate(null, payload));
    }

    const finalResult = await dispatch(AuditActions.markAsRevised({ transactionsIds, currentRoute }));

    if (finalResult) refetchData();

    return finalResult;
  }, [
    xeroBusiness,
    dispatch,
    currentRoute,
    refetchData,
    transactionsData,
    closedBookDateTransactionIds,
    selectedBusinessBookCloseDate,
    showCommonModal,
    messages,
    selectedBusinessIntegrationServiceData.label,
    uiTexts.warning,
    uiTexts.selectContact
  ]);

  const onBulkActionsEdit = useCallback(async(update) => {
    const newTransactionsState = transactionsState.map((state) => {
      if (!selectedIds.includes(state.id)) return state;

      const transactionData = Arrays.findById(transactionsData, state.id);

      const { type: transactionType, extraData, usesItems } = transactionData || {};

      const advancedType = Transactions.getTransactionAdvancedType({ type: transactionType, extraData });

      const allowAddressChanges = advancedType !== AdvancedTransactionTypes.TRANSFER;

      const allowCategoryChanges = !usesItems
        && advancedType !== AdvancedTransactionTypes.BILL_PAYMENT
        && advancedType !== AdvancedTransactionTypes.RECEIVED_PAYMENT;

      const allowExtraInput = advancedType !== AdvancedTransactionTypes.TRANSFER
        && advancedType !== AdvancedTransactionTypes.BILL_PAYMENT
        && advancedType !== AdvancedTransactionTypes.RECEIVED_PAYMENT;

      const updatedState = { ...state, ...update };

      const fieldsToEdit = Object.values(TransactionFieldsToEdit).filter((fieldName) => {
        switch (fieldName) {
          case TransactionFieldsToEdit.ADDRESS:
          case TransactionFieldsToEdit.VENDOR_ID:
            return allowAddressChanges;
          case TransactionFieldsToEdit.CATEGORY:
            return allowCategoryChanges;
          case TransactionFieldsToEdit.ITEM:
            return true;
          case TransactionFieldsToEdit.CLASS:
          case TransactionFieldsToEdit.LOCATION:
          case TransactionFieldsToEdit.PROJECT:
          case TransactionFieldsToEdit.TAX_RATE:
            return allowExtraInput;
          case TransactionFieldsToEdit.TAGS:
            return (allowAddressChanges || !updatedState.address) && (allowCategoryChanges || !updatedState.category);
          default:
            return false;
        }
      });

      return { ...state, ...Objects.getProps(updatedState, fieldsToEdit) };
    });

    setTransactionsState(newTransactionsState);
    if (update.markAsCorrect) {
      const result = await bulkMarkAsRevised(selectedIds, newTransactionsState);

      if (result) setSelectedIds((prev) => prev.filter((id) => !selectedIds.includes(id)));

      return;
    }
    setSelectedIds([]);
  }, [transactionsState, selectedIds, transactionsData, bulkMarkAsRevised]);

  const onTransactionChange = useCallback((id, newData) => {
    setTransactionsState((prevState) => {
      return Arrays.updateItemById(prevState, id, (prevItem) => ({ ...prevItem, ...newData }));
    });
  }, [setTransactionsState]);

  const onBulkActionsCancel = useCallback(() => {
    setSelectedIds([]);
  }, [setSelectedIds]);

  const onHeaderCheckBoxChange = useCallback(() => {
    setSelectedIds(allItemsSelected ? [] : transactionIds);
  }, [transactionIds, allItemsSelected, setSelectedIds]);

  useEffect(() => {
    setTransactionsState((prevState) => {
      return updateTransactionsState({
        prevState,
        data: transactionsData,
        rpaExcludedWords,
        lastGptAiFineTuneStatus
      });
    });
  }, [contactsData, xeroBusiness, integrationService, lastGptAiFineTuneStatus, rpaExcludedWords, transactionsData]);
  useEffect(() => {
    setSelectedIds((prev) => {
      return prev.filter((id) => transactionsData?.some((transaction) => transaction.id === id));
    });
  }, [transactionsData]);

  return {
    transactionsState,
    selectedIds,
    transactionIds,
    closedBookDateTransactionIds,
    allItemsSelected,
    setSelectedIds,
    setTransactionsState,
    bulkMarkAsRevised,
    onBulkActionsEdit,
    onTransactionChange,
    onBulkActionsCancel,
    onHeaderCheckBoxChange
  };
};

export default useAuditUtils;
